export function isColorDarkOrClear(hex: string): "clear" | "dark" {
  // Ensure the hex is in the correct format
  const hexColor = hex.replace("#", "");

  if (hexColor.length !== 6) {
    throw new Error("Invalid hex color format");
  }

  // Convert hex to RGB
  const r: number = parseInt(hexColor.substring(0, 2), 16);
  const g: number = parseInt(hexColor.substring(2, 4), 16);
  const b: number = parseInt(hexColor.substring(4, 6), 16);

  // Calculate the relative luminance using the sRGB formula
  const luminance: number = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Determine if the color is clear (light) or dark based on the luminance threshold
  return luminance > 128 ? "clear" : "dark";
}

export function isImageDarkOrClear(
  imageUrl: string,
  samplingThreshold: number = 0.1
): Promise<"clear" | "dark"> {
  return new Promise((resolve, reject) => {
    if (samplingThreshold < 0 || samplingThreshold > 1) {
      reject("Sampling threshold must be between 0 and 1");
      return;
    }

    const img = new Image();
    img.crossOrigin = "Anonymous"; // Allow cross-origin image processing
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        reject("Canvas context not available");
        return;
      }

      // Set canvas size to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image onto the canvas
      context.drawImage(img, 0, 0, img.width, img.height);

      // Get the image data (pixel data)
      const imageData = context.getImageData(0, 0, img.width, img.height);
      const pixels = imageData.data;
      const totalPixels = img.width * img.height;

      // Calculate the sample step based on the sampling threshold
      const pixelsToSample = Math.max(
        1,
        Math.floor(totalPixels * samplingThreshold)
      ); // Ensure at least 1 pixel is sampled
      const sampleStep = Math.floor(totalPixels / pixelsToSample); // Determine how often to sample

      let totalLuminance = 0;
      let sampledPixels = 0;

      // Loop through the pixels based on the calculated sampling step
      for (let i = 0; i < totalPixels * 4; i += sampleStep * 4) {
        // Increment by sampleStep in the array
        const r = pixels[i]; // Red channel
        const g = pixels[i + 1]; // Green channel
        const b = pixels[i + 2]; // Blue channel

        // Calculate luminance using the sRGB formula
        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        totalLuminance += luminance;
        sampledPixels++;
      }

      // Calculate average luminance
      const avgLuminance = totalLuminance / sampledPixels;

      // Decide if the image is clear or dark based on average luminance
      const result = avgLuminance > 128 ? "clear" : "dark";
      resolve(result);
    };

    img.onerror = () => {
      reject("Failed to load the image");
    };
  });
}
